import {
  Button,
  Card,
  EllipsisMenuButton,
  Flex,
  Menu,
  MenuContent,
  MenuTrigger,
  Spinner,
  Text,
} from '@optiaxiom/react';
import { IconArrowRotateLeft, IconCopy, IconEye, IconXmark } from '@optimizely/axiom-icons';

import { ConfirmationModal } from '@frontend/components/confirmationModal/components/ConfirmationModal';
import type { AgentVersionSummary } from '@frontend/types';
import { formatDate } from '@frontend/utils/dateUtils';

import { getVersionDisplayLabel, type UseAgentVersionsReturn } from '../../../hooks';

type AgentVersionHistorySidebarProps = {
  agentVersions: UseAgentVersionsReturn;
  onClose: () => void;
  onSelectVersion?: (version: AgentVersionSummary | null) => void;
  isSystemAgent?: boolean;
};

function SidebarLayout({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return (
    <Flex flexDirection="column" h="full" p="16">
      <Flex flexDirection="row" justifyContent="space-between" alignItems="center">
        <Text fontWeight="600" fontSize="lg">
          Version History
        </Text>
        <Button
          icon={<IconXmark size="16" />}
          appearance="subtle"
          onClick={onClose}
          aria-label="Close sidebar"
        />
      </Flex>
      <Flex flexDirection="column" gap="16" overflow="auto" flex="1">
        {children}
      </Flex>
    </Flex>
  );
}

function VersionItem({
  version,
  displayLabel,
  isSelected,
  showRestoreOption,
  onSelect,
  onDuplicate,
  onRestore,
}: {
  version: AgentVersionSummary;
  displayLabel: string;
  isSelected: boolean;
  showRestoreOption: boolean;
  onSelect: () => void;
  onDuplicate: () => void;
  onRestore: () => void;
}) {
  return (
    <Card
      border="1"
      borderColor={isSelected ? 'border.focus' : 'border.default'}
      cursor="pointer"
      p="12"
      onClick={onSelect}
    >
      <Flex flexDirection="row" justifyContent="space-between" alignItems="center" w="full">
        <Flex flexDirection="column" gap="4">
          <Text fontWeight="500">{displayLabel}</Text>
          <Text fontSize="sm" color="fg.secondary">
            {formatDate(version.changed_at)}
          </Text>
        </Flex>
        <Menu
          options={[
            {
              label: 'View this version',
              addon: <IconEye size="2xs" />,
              execute: onSelect,
            },
            ...(showRestoreOption
              ? [
                  {
                    label: 'Restore this version',
                    addon: <IconArrowRotateLeft size="2xs" />,
                    execute: onRestore,
                  },
                ]
              : []),
            {
              label: 'Duplicate as a new agent',
              addon: <IconCopy size="2xs" />,
              execute: onDuplicate,
            },
          ]}
        >
          <MenuTrigger asChild>
            <EllipsisMenuButton
              appearance="subtle"
              aria-label="Version actions"
              onClick={e => e.stopPropagation()}
            />
          </MenuTrigger>
          <MenuContent onClick={e => e.stopPropagation()} />
        </Menu>
      </Flex>
    </Card>
  );
}

export function AgentVersionHistorySidebar({
  agentVersions,
  onClose,
  onSelectVersion,
  isSystemAgent = false,
}: AgentVersionHistorySidebarProps) {
  const {
    versions,
    isLoading,
    error,
    selectedVersionSummary,
    selectVersion,
    handleDuplicate,
    restoreDialog,
  } = agentVersions;

  const handleSelectVersion = (version: AgentVersionSummary, index: number) => {
    const result = selectVersion(version, index);
    onSelectVersion?.(result.version);
  };

  // Derive selected version for highlighting
  const selectedInternalVersion =
    selectedVersionSummary?.internal_version ?? versions[0]?.internal_version;

  if (isLoading) {
    return (
      <SidebarLayout onClose={onClose}>
        <Flex justifyContent="center" alignItems="center" py="32">
          <Spinner />
        </Flex>
      </SidebarLayout>
    );
  }

  if (error) {
    return (
      <SidebarLayout onClose={onClose}>
        <Text fontSize="md" color="fg.error">
          Failed to load version history
        </Text>
      </SidebarLayout>
    );
  }

  if (versions.length === 0) {
    return (
      <SidebarLayout onClose={onClose}>
        <Text fontSize="md" color="fg.tertiary">
          No version history available
        </Text>
      </SidebarLayout>
    );
  }

  const currentVersion = versions[0];
  const currentVersionLabel = getVersionDisplayLabel(currentVersion, versions.length, 0);
  const previousVersions = versions.slice(1);

  return (
    <SidebarLayout onClose={onClose}>
      <Text fontWeight="400" color="fg.secondary" fontSize="md">
        Current Version
      </Text>
      <VersionItem
        version={currentVersion}
        displayLabel={currentVersionLabel}
        isSelected={selectedInternalVersion === currentVersion.internal_version}
        showRestoreOption={false}
        onSelect={() => handleSelectVersion(currentVersion, 0)}
        onDuplicate={() => handleDuplicate(currentVersion, currentVersionLabel)}
        onRestore={() => restoreDialog.onOpen(currentVersion, currentVersionLabel)}
      />

      {previousVersions.length > 0 && (
        <>
          <Text fontSize="md" fontWeight="400" color="fg.secondary" pt="8">
            Previous Versions
          </Text>
          <Flex flexDirection="column" gap="12" overflowY="auto" borderT="1" pt="8">
            {previousVersions.map((version, idx) => {
              const index = idx + 1;
              const displayLabel = getVersionDisplayLabel(version, versions.length, index);
              return (
                <VersionItem
                  key={version.internal_version}
                  version={version}
                  displayLabel={displayLabel}
                  isSelected={selectedInternalVersion === version.internal_version}
                  showRestoreOption={!isSystemAgent}
                  onSelect={() => handleSelectVersion(version, index)}
                  onDuplicate={() => handleDuplicate(version, displayLabel)}
                  onRestore={() => restoreDialog.onOpen(version, displayLabel)}
                />
              );
            })}
          </Flex>
        </>
      )}

      <ConfirmationModal
        isShown={restoreDialog.isOpen}
        title="Restore Version"
        onCancel={restoreDialog.onCancel}
        onConfirm={restoreDialog.onConfirm}
        action="Restore Version"
        confirmLoading={restoreDialog.isLoading}
        size="md"
      >
        <Text>
          Restoring this version will replace your current setup. A new version will be created.
        </Text>
      </ConfirmationModal>
    </SidebarLayout>
  );
}
